// WebSocket Client Singleton with Auto-Reconnect
// Authentication: Session cookie is sent automatically by the browser for same-origin WebSocket connections.
// No explicit credentials configuration needed - the connect.sid cookie is included in the upgrade request.

type ConnectionState = 'connected' | 'disconnected' | 'reconnecting';

interface WebSocketMessage<T = unknown> {
  type: string;
  payload: T;
  timestamp: string;
}

const MIN_RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;

class WebSocketClient extends EventTarget {
  private ws: WebSocket | null = null;
  private reconnectDelay = MIN_RECONNECT_DELAY;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private state: ConnectionState = 'disconnected';
  private url: string;
  private shouldReconnect = true;

  constructor(url: string) {
    super();
    this.url = url;
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.shouldReconnect = true;
    this.createConnection();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState('disconnected');
  }

  send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  sendVoiceCallStart(skillId?: string, conversationId?: number): void {
    this.send({
      type: 'voice:call:start',
      payload: { skillId, conversationId },
      timestamp: new Date().toISOString(),
    });
  }

  sendVoiceAudioChunk(sessionId: string, audioBase64: string): void {
    this.send({
      type: 'voice:audio:chunk',
      payload: { sessionId, audio: audioBase64 },
      timestamp: new Date().toISOString(),
    });
  }

  sendVoiceCallEnd(sessionId: string): void {
    this.send({
      type: 'voice:call:end',
      payload: { sessionId },
      timestamp: new Date().toISOString(),
    });
  }

  sendVoiceTtsStop(sessionId: string): void {
    this.send({
      type: 'voice:tts:stop',
      payload: { sessionId },
      timestamp: new Date().toISOString(),
    });
  }

  sendVoiceTextMessage(sessionId: string, text: string): void {
    this.send({
      type: 'voice:text:send',
      payload: { sessionId, text },
      timestamp: new Date().toISOString(),
    });
  }

  sendChatMessage(conversationId: number, content: string, model?: 'sonnet' | 'opus', attachmentIds?: number[]): void {
    this.send({
      type: 'chat:send',
      payload: { conversationId, content, model, attachmentIds },
      timestamp: new Date().toISOString(),
    });
  }

  private createConnection(): void {
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.addEventListener('open', () => {
      this.reconnectDelay = MIN_RECONNECT_DELAY;
      this.setState('connected');
    });

    this.ws.addEventListener('message', (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string) as WebSocketMessage;
        this.dispatchEvent(new CustomEvent('ws-message', { detail: message }));
      } catch {
        // Ignore unparseable messages
      }
    });

    this.ws.addEventListener('close', () => {
      this.ws = null;
      if (this.shouldReconnect) {
        this.setState('reconnecting');
        this.scheduleReconnect();
      } else {
        this.setState('disconnected');
      }
    });

    this.ws.addEventListener('error', () => {
      // Error is followed by close, so reconnect logic is handled there
    });
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.createConnection();
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_DELAY);
    }, this.reconnectDelay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(newState: ConnectionState): void {
    if (this.state === newState) return;
    this.state = newState;
    this.dispatchEvent(new CustomEvent('connection-state', { detail: newState }));
  }
}

// Build WebSocket URL based on current location
function buildWsUrl(): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/ws`;
}

// Singleton instance
export const wsClient = new WebSocketClient(buildWsUrl());

// Auto-connect on module load
wsClient.connect();
